import React, { useState, useEffect } from 'react';
import { 
  getDeletedDispatches, 
  getDeletedBatches, 
  restoreDispatch, 
  restoreBatch 
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Loader2, 
  Trash2, 
  RotateCcw, 
  Truck, 
  Layers,
  Search,
  AlertTriangle,
  Calendar,
  User
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const DELETION_REASONS = {
  duplicate: 'Duplicate Entry',
  error: 'Data Error',
  cancelled: 'Cancelled',
  qc_failed: 'QC Failed',
  customer_deleted: 'Customer Deleted',
  other: 'Other'
};

export default function DeletedItems() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dispatches');
  const [deletedDispatches, setDeletedDispatches] = useState([]);
  const [deletedBatches, setDeletedBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  
  // Restore dialog
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [restoreType, setRestoreType] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dispatchesRes, batchesRes] = await Promise.allSettled([
        getDeletedDispatches(),
        getDeletedBatches()
      ]);
      if (dispatchesRes.status === 'fulfilled') setDeletedDispatches(dispatchesRes.value.data?.dispatches || []);
      if (batchesRes.status === 'fulfilled') setDeletedBatches(batchesRes.value.data?.batches || []);
    } catch (error) {
      toast.error('Failed to load deleted items');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (item, type) => {
    setItemToRestore(item);
    setRestoreType(type);
    setRestoreDialogOpen(true);
  };

  const confirmRestore = async () => {
    if (!itemToRestore || !restoreType) return;
    
    setRestoring(true);
    try {
      if (restoreType === 'dispatch') {
        await restoreDispatch(itemToRestore.id);
        toast.success(`Dispatch ${itemToRestore.dispatch_number} restored`);
      } else {
        await restoreBatch(itemToRestore.id);
        toast.success(`Batch ${itemToRestore.batch_code} restored`);
      }
      setRestoreDialogOpen(false);
      setItemToRestore(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore item');
    } finally {
      setRestoring(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  // Filter items
  const filteredDispatches = deletedDispatches.filter(d => {
    const matchesSearch = search === '' || 
      d.dispatch_number?.toLowerCase().includes(search.toLowerCase()) ||
      d.customer_name?.toLowerCase().includes(search.toLowerCase());
    const matchesReason = reasonFilter === 'all' || d.deletion_reason === reasonFilter;
    return matchesSearch && matchesReason;
  });

  const filteredBatches = deletedBatches.filter(b => {
    const matchesSearch = search === '' || 
      b.batch_code?.toLowerCase().includes(search.toLowerCase()) ||
      b.product_name?.toLowerCase().includes(search.toLowerCase());
    const matchesReason = reasonFilter === 'all' || b.deletion_reason === reasonFilter;
    return matchesSearch && matchesReason;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deleted-items-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Deleted Items
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and restore soft-deleted dispatches and batches
          </p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1 w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search deleted items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-deleted"
              />
            </div>
            <div className="flex gap-2 items-center">
              <Label className="text-sm whitespace-nowrap">Reason:</Label>
              <Select value={reasonFilter} onValueChange={setReasonFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reasons</SelectItem>
                  {Object.entries(DELETION_REASONS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        <Button
          variant={activeTab === 'dispatches' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('dispatches')}
          className="gap-2"
          data-testid="tab-dispatches"
        >
          <Truck className="w-4 h-4" />
          Dispatches ({filteredDispatches.length})
        </Button>
        <Button
          variant={activeTab === 'batches' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('batches')}
          className="gap-2"
          data-testid="tab-batches"
        >
          <Layers className="w-4 h-4" />
          Batches ({filteredBatches.length})
        </Button>
      </div>

      {/* Content */}
      {activeTab === 'dispatches' && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Deleted Dispatches</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredDispatches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Dispatch #</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Customer</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Reason</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Deleted By</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Deleted At</th>
                      <th className="w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredDispatches.map((dispatch) => (
                      <tr key={dispatch.id} className="hover:bg-muted/30" data-testid={`deleted-dispatch-${dispatch.id}`}>
                        <td className="px-4 py-3 font-mono text-sm">{dispatch.dispatch_number}</td>
                        <td className="px-4 py-3 text-sm">{dispatch.customer_name || '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm">{dispatch.dispatch_date}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            {DELETION_REASONS[dispatch.deletion_reason] || dispatch.deletion_reason}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{dispatch.deleted_by || '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {formatDateTime(dispatch.deleted_at)}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestore(dispatch, 'dispatch')}
                            className="h-8"
                            data-testid={`restore-dispatch-${dispatch.id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restore
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">No deleted dispatches</h3>
                <p className="text-sm text-muted-foreground">
                  {search || reasonFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Deleted dispatches will appear here'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'batches' && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Deleted Batches</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredBatches.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Batch Code</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Product</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Qty Produced</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Reason</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Deleted By</th>
                      <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Deleted At</th>
                      <th className="w-24"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBatches.map((batch) => (
                      <tr key={batch.id} className="hover:bg-muted/30" data-testid={`deleted-batch-${batch.id}`}>
                        <td className="px-4 py-3 font-mono text-sm">{batch.batch_code}</td>
                        <td className="px-4 py-3 text-sm">{batch.product_name || '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm">{batch.quantity_produced?.toFixed(0) || '0'}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            {DELETION_REASONS[batch.deletion_reason] || batch.deletion_reason}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{batch.deleted_by || '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {formatDateTime(batch.deleted_at)}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestore(batch, 'batch')}
                            className="h-8"
                            data-testid={`restore-batch-${batch.id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restore
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Layers className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-medium mb-2">No deleted batches</h3>
                <p className="text-sm text-muted-foreground">
                  {search || reasonFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Deleted batches will appear here'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deletion Notes */}
      {((activeTab === 'dispatches' && filteredDispatches.some(d => d.deletion_notes)) ||
        (activeTab === 'batches' && filteredBatches.some(b => b.deletion_notes))) && (
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader className="py-3">
            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Deletion Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="space-y-2 text-sm text-amber-700">
              {activeTab === 'dispatches' && filteredDispatches.filter(d => d.deletion_notes).map(d => (
                <div key={d.id} className="flex gap-2">
                  <span className="font-mono">{d.dispatch_number}:</span>
                  <span>{d.deletion_notes}</span>
                </div>
              ))}
              {activeTab === 'batches' && filteredBatches.filter(b => b.deletion_notes).map(b => (
                <div key={b.id} className="flex gap-2">
                  <span className="font-mono">{b.batch_code}:</span>
                  <span>{b.deletion_notes}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-600" />
              Restore {restoreType === 'dispatch' ? 'Dispatch' : 'Batch'}
            </DialogTitle>
            <DialogDescription>
              {restoreType === 'dispatch' 
                ? `Restore dispatch ${itemToRestore?.dispatch_number}?`
                : `Restore batch ${itemToRestore?.batch_code}?`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg space-y-2">
              {restoreType === 'dispatch' && itemToRestore && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Customer:</span>
                    <span className="font-medium">{itemToRestore.customer_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Date:</span>
                    <span className="font-mono">{itemToRestore.dispatch_date}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Deleted Reason:</span>
                    <span>{DELETION_REASONS[itemToRestore.deletion_reason] || itemToRestore.deletion_reason}</span>
                  </div>
                </>
              )}
              {restoreType === 'batch' && itemToRestore && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Product:</span>
                    <span className="font-medium">{itemToRestore.product_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Quantity:</span>
                    <span className="font-mono">{itemToRestore.quantity_produced?.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Deleted Reason:</span>
                    <span>{DELETION_REASONS[itemToRestore.deletion_reason] || itemToRestore.deletion_reason}</span>
                  </div>
                </>
              )}
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              This will restore the item to its previous state. 
              {restoreType === 'dispatch' && ' Stock allocations will be re-applied if batches are still available.'}
              {restoreType === 'batch' && ' The batch will be available for dispatches again.'}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
              Cancel
            </Button>
            <Button onClick={confirmRestore} disabled={restoring} className="bg-green-600 hover:bg-green-700">
              {restoring ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-2" /> Restore</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
